import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { BG_OFFWHITE, MID_GREY } from '../../../utils/colours';
import { Link } from 'react-router-dom';
import './TreeDiagram.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from '@nextui-org/react';

const treeData = {
  name: 'Root',
  meetingCount: 10,
  children: [
    { name: 'Child 1', meetingCount: 5 },
    { name: 'Child 2', meetingCount: 15 },
    { name: 'Child 3', meetingCount: 7 },
    { name: 'Child 4', meetingCount: 8 },
    { name: 'Child 5', meetingCount: 2 },
    { name: 'Child 6', meetingCount: 9 },
  ],
};

const Label = ({ 
    x, 
    y, 
    linkPath,
    name, 
    meetingCount,
    isParent,
    focusName
 }) => {
    
    if (isParent) {
        return null;
    }
    
    return (
        <div
            style={{
                position: 'absolute',
                left: x - 13,
                top: y - 36
            }}
        >
            <Link to={`${linkPath}?name=${encodeURIComponent(name)}`} 
                className='flex gap-1 items-center'
            >
                <Tooltip
                    content={`${meetingCount} meeting${meetingCount > 1 ? 's' : ''} with ${focusName} in the past year`}
                    className='bg-black text-white'
                    size='sm'
                >
                    <div className='flex gap-0.5 items-center'>
                        <FontAwesomeIcon 
                            icon={faCalendar}
                            color={MID_GREY}
                            size={'xs'}
                        /> 
                        <span className='text-xs font-semibold opacity-70'>
                            {meetingCount}
                        </span>
                    </div>
                </Tooltip>
                
                <p
                    className='text-xs font-medium max-w-96 truncate'
                    style={{
                        // background: 'red',
                        textShadow: '#FFF 0px 0 1px, #FFF 0px 0 2px, #FFF 0px 0 2px, #FFF 1px 0 2px, #FFF 1px 0 3px, #FFF 1px 0 4px, #FFF 1px 0 5px, #FFF 1px 0 6px, #FFF 1px 0 7px, #FFF 1px 0 6px, #FFF 1px 0 8px, #FFF 1px 0 6px, #FFF 1px 0 10px'
                    }}
                >
                    meeting{meetingCount > 1 ? 's' : ''} with {name} in the past year
                </p>
               
            </Link>
        </div>
        
    );
};

const getWidthWithoutPadding = (rect) => {
    const computedStyle = window.getComputedStyle(rect);
    const widthWithoutPadding = 
      rect.clientWidth - 
      parseFloat(computedStyle.paddingLeft) - 
      parseFloat(computedStyle.paddingRight);
    return widthWithoutPadding;
}

const TreeDiagram = ({ 
    name,
    connections,
    totalRelationships,
    linkPath,
    seeAllHandler,
    sidePeekIsOpen
}) => {

  const svgRef = useRef();
  const containerRef = useRef();
  const [containerWidth, setContainerWidth] = useState(800); // Initial width
  const [nodes, setNodes] = useState([]); // State to store nodes for labels

  const data = {
    name: name,
    meetingCount: null,
    children: connections.filter((connection, i) => i < 9).map(connection => {
        return {
            name: connection.clientName,
            meetingCount: connection.meetings
        }
    })
  }

  useEffect(() => {

    // Function to update container width
    const updateWidth = () => {
        if (containerRef.current) {
            const widthWithoutPadding = getWidthWithoutPadding(containerRef.current);
            setContainerWidth(widthWithoutPadding);
        }
    };

    // Set initial width
    updateWidth();

    // Add event listener for window resize
    window.addEventListener('resize', updateWidth);

    // Clean up event listener on component unmount
    return () => window.removeEventListener('resize', updateWidth);
  }, [sidePeekIsOpen]);

  useEffect(() => {
    if (containerRef.current && sidePeekIsOpen) {
        const widthWithoutPadding = getWidthWithoutPadding(containerRef.current) - 100;
        setContainerWidth(widthWithoutPadding);
    }

  }, [sidePeekIsOpen]);


  useEffect(() => {
    if (!data) return;

    // Set up the SVG container
    const height = 400; // Keep the height fixed for now
    const width = containerWidth; // Use the dynamic container width

    // const containerWidth = containerRef.current.offsetWidth;
    // const margin = { top: 20, right: 30, bottom: 40, left: 40 };
    // const innerWidth = containerWidth - margin.left - margin.right;

    // Clear previous contents of SVG to avoid overlapping elements
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', 'translate(-10, 0)'); // Adjusted translate to avoid negative values

    // Create a D3 hierarchical data structure
    const root = d3.hierarchy(data);

    // Create a tree layout and set orientation to right-to-left
    const treeLayout = d3.tree().size([height, width - 14]); // Adjusted layout size
    treeLayout(root);

    // Flip the x-coordinate to create a right-to-left layout
    root.descendants().forEach(d => {
      d.y = width - d.y; // Flip the horizontal coordinates
    });

    // Generate curved (diagonal) links between nodes
    const linkGenerator = d3.linkHorizontal()
      .x(d => d.y)
      .y(d => d.x);

    // Define a gradient
    const gradient = svg.append('defs')
        .append('linearGradient')
        .attr('id', 'line-gradient')
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '0%');

    // Add color stops to the gradient
    gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', '#c5c4aa');

    gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', MID_GREY);

    // Create links (curved branches) and weight them by `meetingCount`
    svg.selectAll('path')
      .data(root.links())
      .enter()
      .append('path')
      .attr('d', linkGenerator)
      .attr('fill', 'none')
      .attr('class', 'treelink')
      .attr('stroke', 'url(#line-gradient)')
      .attr('stroke-width', 2);

    // Create nodes (circles)
    svg.selectAll('circle')
      .data(root.descendants())
      .enter()
      .append('circle')
      .attr('cx', d => d.y)
      .attr('cy', d => d.x)
      .attr('r', 3) // Fixed size for nodes
      .attr('fill', d => d.children ? MID_GREY : '#c5c4aa');

    // Add labels to nodes
    svg.selectAll('text')
        .data(root.descendants())
        .enter()
        .append('text')
        .attr('x', d => d.y + 3) // Keep the x-coordinate aligned with the node
        .attr('y', d => d.x + 18) // Position text below the node
        .attr('text-anchor', d => 'end') // Left-align parents, right-align children
        .attr('class', d => d.children ? 'text-xs font-medium' : 'hidden')
        .text(d => name);

        
    // Save node data for labels
    if (nodes.length === 0) {
        setNodes(root.descendants());
    }

  }, [data, containerWidth, name, connections]); // Re-run the effect when data or containerWidth changes

  const hasMoreLinks = totalRelationships > 10;

  const onClickSeeMore = () => {
    seeAllHandler();
  }

  return (
    <div style={{ 
        width: '100%', 
        position: 'relative',
        opacity: containerRef.current ? 100 : 0,
        transition: '0.3s opacity' 
    }}>
        <div ref={containerRef} >
            <svg ref={svgRef} />
            {nodes.map((node, index) => (
                <Label
                    key={index}
                    x={node.y}
                    y={node.x + 15} // Position text below the node
                    linkPath={linkPath}
                    name={node.data.name}
                    meetingCount={node.data.meetingCount}
                    isParent={node.children ? true : false}
                    focusName={name}
                />
                ))}

            {hasMoreLinks && (
                <div className='flex'>
                    <p 
                        onClick={onClickSeeMore}
                        className='text-xs font-medium -mt-2 opacity-60 hover:opacity-100 cursor-pointer'
                    >
                        See all
                    </p>
                </div>
            )}
        </div>
    </div>
  );
};

export default TreeDiagram;
